/**
 * Thin wrappers around Supabase Auth MFA APIs.
 * Every call is try/catch'd and returns a normalized shape so callers can
 * fail-safe without crashing the login flow.
 */
import { supabase } from "@/integrations/supabase/client";

export type AalLevel = "aal1" | "aal2" | null;

export async function getAal(): Promise<{ current: AalLevel; next: AalLevel }> {
  try {
    const { data, error } =
      await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (error) throw error;
    return {
      current: (data?.currentLevel as AalLevel) ?? null,
      next: (data?.nextLevel as AalLevel) ?? null,
    };
  } catch (err) {
    console.warn("[mfa] getAal failed (failing open):", err);
    return { current: null, next: null };
  }
}

export async function listFactors() {
  try {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) throw error;
    const totp = (data?.totp ?? []) as any[];
    const verified = totp.filter((f) => f.status === "verified");
    return { all: totp, verified };
  } catch (err) {
    console.warn("[mfa] listFactors failed:", err);
    return { all: [], verified: [] };
  }
}

export async function enrollTotp(friendlyName = "Authenticator App") {
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: "totp",
    friendlyName: `${friendlyName} ${new Date().toISOString().slice(0, 10)}`,
  });
  if (error) throw error;
  return data; // { id, type, totp: { qr_code, secret, uri } }
}

export async function verifyEnrollment(factorId: string, code: string) {
  const { data: chal, error: chalErr } = await supabase.auth.mfa.challenge({
    factorId,
  });
  if (chalErr) throw chalErr;
  const { data, error } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: chal.id,
    code,
  });
  if (error) throw error;
  // Mark enrolled in our tracking table (best effort).
  try {
    await supabase
      .from("user_mfa_status")
      .upsert({
        user_id: (await supabase.auth.getUser()).data.user?.id ?? "",
        enrolled: true,
        enrolled_at: new Date().toISOString(),
        last_verified_at: new Date().toISOString(),
      });
  } catch (err) {
    console.warn("[mfa] could not update user_mfa_status:", err);
  }
  return data;
}

export async function challengeAndVerify(factorId: string, code: string) {
  const { data: chal, error: chalErr } = await supabase.auth.mfa.challenge({
    factorId,
  });
  if (chalErr) throw chalErr;
  const { data, error } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: chal.id,
    code,
  });
  if (error) throw error;
  try {
    const uid = (await supabase.auth.getUser()).data.user?.id;
    if (uid) {
      await supabase
        .from("user_mfa_status")
        .update({ last_verified_at: new Date().toISOString() })
        .eq("user_id", uid);
    }
  } catch {
    /* non-fatal */
  }
  return data;
}

export async function unenrollFactor(factorId: string) {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) throw error;
  try {
    const uid = (await supabase.auth.getUser()).data.user?.id;
    if (uid) {
      await supabase
        .from("user_mfa_status")
        .update({ enrolled: false })
        .eq("user_id", uid);
    }
  } catch {
    /* non-fatal */
  }
}

export async function getMyMfaStatus() {
  try {
    const { data, error } = await supabase.rpc("mfa_get_my_status");
    if (error) throw error;
    return data as {
      user_id: string;
      enrolled: boolean;
      enrolled_at: string | null;
      last_verified_at: string | null;
      enforcement_deferred_until: string | null;
      mfa_required_after: string | null;
      mfa_enforced: boolean;
    } | null;
  } catch (err) {
    console.warn("[mfa] getMyMfaStatus failed:", err);
    return null;
  }
}

export async function setMfaRequiredAfter(iso: string) {
  try {
    await supabase.rpc("mfa_set_required_after", { p_required_after: iso });
  } catch (err) {
    console.warn("[mfa] setMfaRequiredAfter failed:", err);
  }
}

export async function markMfaEnforced() {
  try {
    await supabase.rpc("mfa_mark_enforced");
  } catch (err) {
    console.warn("[mfa] markMfaEnforced failed:", err);
  }
}
